import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useMatch, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faComments, faCalendarDays, faUser, faRightFromBracket, faListCheck
} from '@fortawesome/free-solid-svg-icons';

import { guestApi, getGuestSession, clearGuestSession } from '../utils/portalApi';
import VoiceMiniPlayer from '../components/VoiceMiniPlayer';
import Avatar from '../components/Avatar';
import GuestMessenger from '../components/guest/GuestMessenger';
import GuestMeetings from '../components/guest/GuestMeetings';
import GuestProfile from '../components/guest/GuestProfile';
import GuestTasks from '../components/guest/GuestTasks';
import GuestTaskDetail from '../components/guest/GuestTaskDetail';
import GuestBell from '../components/guest/GuestBell';
import GuestPassword from '../components/guest/GuestPassword';
import '../styles/messenger.css';
import '../styles/portal.css';
import '../styles/guest.css';

/**
 * A guest's whole view of the company (guest role, Phase 1).
 *
 * Rendered outside ProtectedRoute and DashboardLayout, like the invite portal:
 * no staff sidebar, no topbar, no search, no employee directory. Its own
 * sidebar has only what a guest can use - their groups, their meetings and
 * their profile - because a menu of modules they cannot open is a menu of
 * errors they cannot read.
 *
 * Every list here comes from the server, built from the guest's session; the
 * page never decides what they may see.
 */

const GuestHome = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [me, setMe] = useState(null);
    // null until the first answer, so "no conversations" is never shown early.
    const [chats, setChats] = useState(null);
    const onMeetings = useMatch('/guest/meetings');
    const onProfile = useMatch('/guest/profile');
    const onTasks = useMatch('/guest/tasks/*');
    const section = onMeetings ? 'meetings' : onProfile ? 'profile' : onTasks ? 'tasks' : 'chats';
    const [openTasks, setOpenTasks] = useState(0);
    const onTaskCounts = useCallback((counts) => setOpenTasks(counts?.open || 0), []);

    // Signed out, switched off or never signed in: back to the sign-in page,
    // with the reason, rather than a page of failed requests.
    const signOut = useCallback((message) => {
        clearGuestSession();
        navigate('/guest/login', { replace: true, state: message ? { message } : undefined });
    }, [navigate]);

    const onAuthError = useCallback((err) => {
        const status = err.response?.status;
        if (status === 401 || (status === 403 && err.response?.data?.disabled)) {
            signOut(err.response?.data?.message);
        }
    }, [signOut]);

    const loadChats = useCallback(() => {
        guestApi.get('/chats').then(({ data }) => setChats(data)).catch(onAuthError);
    }, [onAuthError]);

    const loadTaskCount = useCallback(() => {
        guestApi.get('/tasks', { params: { view: 'open' } })
            .then(({ data }) => onTaskCounts(data.counts)).catch(() => { });
    }, [onTaskCounts]);

    // Something happened somewhere: a message, a task change.
    const onActivity = useCallback(() => { loadChats(); loadTaskCount(); }, [loadChats, loadTaskCount]);

    useEffect(() => {
        // Not signed in: come back here (an emailed link to a task, say) after.
        if (!getGuestSession()) {
            navigate('/guest/login', { replace: true, state: { from: location.pathname } });
            return undefined;
        }
        guestApi.get('/me').then(({ data }) => setMe(data)).catch(onAuthError);
        loadChats();
        loadTaskCount();
        const timer = setInterval(loadChats, 20000);
        return () => clearInterval(timer);
        // Only on arrival: the path at that moment is where to come back to.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadChats, loadTaskCount, onAuthError, signOut]);

    if (!me) {
        return (
            <div className="portal-shell">
                <div className="portal-card notice">
                    <div className="portal-spinner" />
                    <p>Opening your guest space…</p>
                </div>
            </div>
        );
    }

    // A password the team chose has passed through somebody else's hands:
    // replace it before anything else.
    if (me.mustChangePassword && !me.viewedByStaff) {
        return (
            <div className="portal-shell">
                <div className="portal-card">
                    <div className="portal-brand">GTS</div>
                    <p className="portal-eyebrow">Guest access</p>
                    <h2>Choose your own password</h2>
                    <GuestPassword me={me} onSaved={setMe} forced />
                    <p className="guest-links">
                        <button type="button" className="guest-link" onClick={() => signOut()}>Sign out</button>
                    </p>
                </div>
            </div>
        );
    }

    const unreadChats = (chats || []).reduce((sum, c) => sum + (c.status === 'active' ? c.unread || 0 : 0), 0);

    return (
        <>
            <VoiceMiniPlayer />
            {me.viewedByStaff && (
                <div className="guest-staff-view" role="status">
                    <span>You are viewing the portal as <strong>{me.name}</strong>. Anything you send goes out as them.</span>
                    <button type="button" onClick={() => { clearGuestSession(); window.close(); navigate('/external-users', { replace: true }); }}>
                        Exit
                    </button>
                </div>
            )}
            <div className={`portal-workspace has-sidebar guest-workspace${me.viewedByStaff ? ' is-staff-view' : ''}`}>
                <nav className="portal-sidebar guest-sidebar" aria-label="Guest navigation">
                    <div className="guest-me">
                        <Avatar name={me.name} profilePic={me.profilePic} className="msgr-avatar" />
                        <div>
                            <strong>{me.name}</strong>
                            <span className="ext-badge">Guest</span>
                        </div>
                        <GuestBell onDisabled={signOut} onActivity={onActivity} />
                    </div>

                    <div className="guest-nav">
                        <button type="button" aria-current={section === 'chats' ? 'page' : undefined}
                            onClick={() => navigate('/guest/chats')}>
                            <FontAwesomeIcon icon={faComments} /> Chats
                            {unreadChats > 0 && <span className="guest-unread guest-nav-count">{unreadChats > 99 ? '99+' : unreadChats}</span>}
                        </button>
                        <button type="button" aria-current={section === 'tasks' ? 'page' : undefined}
                            onClick={() => navigate('/guest/tasks')}>
                            <FontAwesomeIcon icon={faListCheck} /> Tasks
                            {openTasks > 0 && <span className="guest-unread guest-nav-count">{openTasks > 99 ? '99+' : openTasks}</span>}
                        </button>
                        {me.meetingsEnabled && (
                            <button type="button" aria-current={section === 'meetings' ? 'page' : undefined}
                                onClick={() => navigate('/guest/meetings')}>
                                <FontAwesomeIcon icon={faCalendarDays} /> Meetings
                            </button>
                        )}
                        <button type="button" aria-current={section === 'profile' ? 'page' : undefined}
                            onClick={() => navigate('/guest/profile')}>
                            <FontAwesomeIcon icon={faUser} /> Profile
                        </button>
                    </div>

                    <button type="button" className="guest-signout" onClick={() => signOut()}>
                        <FontAwesomeIcon icon={faRightFromBracket} /> Sign out
                    </button>
                </nav>

                <Routes>
                    <Route path="chats" element={<GuestMessenger chats={chats} onActivity={onActivity} />} />
                    <Route path="chats/:conversationId" element={<GuestMessenger chats={chats} onActivity={onActivity} />} />
                    <Route path="tasks" element={<GuestTasks onAuthError={onAuthError} onCounts={onTaskCounts} />} />
                    <Route path="tasks/:taskId" element={<GuestTaskDetail onAuthError={onAuthError} onChanged={loadTaskCount} />} />
                    {me.meetingsEnabled && <Route path="meetings" element={<GuestMeetings onAuthError={onAuthError} />} />}
                    <Route path="profile" element={<GuestProfile me={me} onSaved={setMe} />} />
                    <Route path="*" element={<Navigate to="/guest/chats" replace />} />
                </Routes>
            </div>
        </>
    );
};

export default GuestHome;
